import {
    ArrowRight,
    BadgeCheck,
    Bookmark,
    Bot,
    Building2,
    CalendarCheck2,
    ChevronLeft,
    ChevronRight,
    CircleDollarSign,
    Clock3,
    LoaderCircle,
    Phone,
    Send,
    ShieldCheck,
    Sparkles,
    Star,
    Ticket,
    User,
    Users,
} from 'lucide-react'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import { BASE_URL_API } from '../../References/URLs'
import styles from './ScheduleADemo.module.css'

const parseJson = (raw) => { try { return JSON.parse(raw) } catch { return null } }

const DEMO_HIGHLIGHTS = [
    {
        icon: Clock3,
        title: 'Live payroll walkthrough',
        body: 'We run a real payroll cycle with your numbers — attendance, commissions, deductions — on the call itself.',
    },
    {
        icon: CircleDollarSign,
        title: 'Commission & compliance setup',
        body: 'PF, ESI, PT — our expert configures everything for your salon during the session.',
    },
    {
        icon: Bot,
        title: 'Meet MUNIM ji',
        body: 'See your 24/7 payroll AI answer real questions — in Hindi, Hinglish, or English — live.',
    },
    {
        icon: Users,
        title: 'Your team. Your structure.',
        body: 'Bring your staff list. We show you exactly how Lokaci Pro handles your specific salary structures.',
    },
]

const TRUST_POINTS = [
    { icon: BadgeCheck, text: '30-minute session — no fluff, only your use case' },
    { icon: ShieldCheck, text: 'No sales pressure. Cancel anytime.' },
    { icon: Star, text: 'Loved by 2,400+ salon owners across India' },
    { icon: CalendarCheck2, text: 'Slots available this week' },
]

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
]

const isSameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()

const buildCalendarGrid = (year, month) => {
    const firstDay = new Date(year, month, 1).getDay()
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const cells = []
    for (let i = 0; i < firstDay; i++) cells.push(null)
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d))
    return cells
}

// ── Utility helpers ───────────────────────────────────────────
const toDateStr = (date) => {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, '0')
    const d = String(date.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
}

const formatSlotTime = (time24) => {
    if (!time24) return time24
    const [h, m] = time24.split(':').map(Number)
    const ampm = h >= 12 ? 'PM' : 'AM'
    const h12 = h % 12 || 12
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`
}

const dispatchErrorCode = (errorCode, message, { refetchSlots, refetchStatus } = {}) => {
    switch (errorCode) {
        case 'BIZ_002':
            toast.error('Slot booking is temporarily unavailable. Please contact support.')
            break
        case 'BIZ_003':
            refetchStatus?.()
            break
        case 'BIZ_004': case 'BIZ_005': case 'BIZ_006': case 'BIZ_007':
            toast.warning('That slot is no longer available. Refreshing...')
            refetchSlots?.()
            break
        case 'BIZ_008':
            toast.warning('That slot just filled up — please pick another.')
            refetchSlots?.()
            break
        case 'BIZ_009':
            refetchStatus?.()
            break
        case 'BIZ_010':
            refetchStatus?.()
            break
        case 'DB_001':
            toast.error('A server error occurred. Please retry.')
            break
        case 'VAL_001': case 'VAL_002':
            console.error(`[ScheduleADemo] validation error ${errorCode}:`, message)
            toast.error('Something went wrong. Please try again.')
            break
        case 'VAL_004':
            toast.info('Booking not found. Refreshing...')
            refetchStatus?.()
            break
        default:
            toast.error(message || 'Something went wrong.')
    }
}

// ── Smart Calendar Picker ──────────────────────────────────
const CalendarPicker = ({ selectedDate, onSelect, availableDates, slotsLoading }) => {
    const today = useMemo(() => {
        const d = new Date(); d.setHours(0, 0, 0, 0); return d
    }, [])

    const [viewYear, setViewYear] = useState(today.getFullYear())
    const [viewMonth, setViewMonth] = useState(today.getMonth())

    const cells = useMemo(() => buildCalendarGrid(viewYear, viewMonth), [viewYear, viewMonth])

    const canGoPrev = viewYear > today.getFullYear() || viewMonth > today.getMonth()

    const prevMonth = () => {
        if (!canGoPrev) return
        if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11) }
        else setViewMonth(m => m - 1)
    }
    const nextMonth = () => {
        if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0) }
        else setViewMonth(m => m + 1)
    }

    const isDisabled = (date) => {
        if (!date) return true
        if (date < today) return true
        if (availableDates && availableDates.size > 0) {
            if (!availableDates.has(toDateStr(date))) return true
        } else if (!slotsLoading) {
            if (date.getDay() === 0) return true   // fallback: Sundays off
        }
        return false
    }

    return (
        <div className={styles.calendarWrap}>
            <div className={styles.calendarNav}>
                <button
                    type="button"
                    className={styles.calNavBtn}
                    onClick={prevMonth}
                    disabled={!canGoPrev}
                    aria-label="Previous month"
                >
                    <ChevronLeft size={16} />
                </button>
                <span className={styles.calMonthLabel}>
                    {MONTH_NAMES[viewMonth]} {viewYear}
                </span>
                <button
                    type="button"
                    className={styles.calNavBtn}
                    onClick={nextMonth}
                    aria-label="Next month"
                >
                    <ChevronRight size={16} />
                </button>
            </div>

            <div className={styles.calGrid}>
                {DAY_NAMES.map(d => (
                    <div key={d} className={styles.calDayName}>{d}</div>
                ))}
                {cells.map((date, i) => {
                    if (!date) return <div key={`e-${i}`} />
                    const disabled = isDisabled(date)
                    const isToday = isSameDay(date, today)
                    const isSelected = selectedDate && isSameDay(date, selectedDate)
                    return (
                        <button
                            key={date.toISOString()}
                            type="button"
                            className={[
                                styles.calDay,
                                disabled ? styles.calDayDisabled : '',
                                isToday && !isSelected ? styles.calDayToday : '',
                                isSelected ? styles.calDaySelected : '',
                            ].join(' ')}
                            onClick={() => !disabled && onSelect(date)}
                            disabled={disabled}
                            aria-label={date.toDateString()}
                        >
                            {date.getDate()}
                            {isToday && <span className={styles.calTodayDot} />}
                        </button>
                    )
                })}
            </div>
        </div>
    )
}

// ── Main Component ─────────────────────────────────────────
const ScheduleADemo = () => {
    const navigate  = useNavigate()
    const asCtx     = useContext(ActiveStoreContext)
    const authCtx   = useContext(AuthContext)

    const activeStore     = useMemo(() => parseJson(asCtx?.activeStore),  [asCtx?.activeStore])
    const loginData       = useMemo(() => parseJson(authCtx?.loginData),   [authCtx?.loginData])
    const storeName       = activeStore?.storeName || activeStore?.businessName || loginData?.storeName || ''
    const storeIdentifier = activeStore?.storeIdentifier || ''

    // ── Page / fetch state ────────────────────────────────────
    const [pageState,       setPageState]       = useState('loading')
    const [existingBooking, setExistingBooking] = useState(null)
    const [fetchError,      setFetchError]      = useState('')

    // ── Available slots (from API) ────────────────────────────
    const [availableSlots, setAvailableSlots] = useState([])    // [{date, slots:[{time,availableCount}]}]
    const [slotsLoading,   setSlotsLoading]   = useState(false)

    // ── Booking form state ────────────────────────────────────
    const [selectedDate, setSelectedDate] = useState(null)
    const [selectedTime, setSelectedTime] = useState('')
    const [message,      setMessage]      = useState('')
    const [contactName,  setContactName]  = useState('')
    const [contactPhone, setContactPhone] = useState('')
    const [contactEmail, setContactEmail] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitted,    setSubmitted]    = useState(false)
    const [confirmationCode, setConfirmationCode] = useState('')

    // ── Reschedule / cancel state ──────────────────────────────
    const [isRescheduling,     setIsRescheduling]     = useState(false)
    const [showCancelConfirm,  setShowCancelConfirm]  = useState(false)
    const [cancellationReason, setCancellationReason] = useState('')
    const [isCancelling,       setIsCancelling]       = useState(false)

    // ── Derived ──────────────────────────────────────────────
    const selectedDateStr = useMemo(() => selectedDate ? toDateStr(selectedDate) : null, [selectedDate])

    const availableDates = useMemo(() => new Set(availableSlots.map(d => d.date)), [availableSlots])

    const slotsForSelectedDate = useMemo(() => {
        if (!selectedDateStr) return []
        const entry = availableSlots.find(d => d.date === selectedDateStr)
        return entry?.slots?.filter(s => s.availableCount > 0) || []
    }, [selectedDateStr, availableSlots])

    const isValid = selectedDate && selectedTime

    // ── Pre-populate contact from authIdentifier ─────────────────
    useEffect(() => {
        const id = authCtx?.authIdentifier || ''
        const stripped = id.replace(/[+\-\s]/g, '')
        if (/^\d{10,12}$/.test(stripped)) setContactPhone(id)
        else if (id.includes('@')) setContactEmail(id)
    }, [authCtx?.authIdentifier])

    const formatDate = (date) =>
        date ? date.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : ''

    // ── API: payrollDemoGetStatus ──────────────────────────────
    const fetchDemoStatus = useCallback(async () => {
        if (!storeIdentifier) return
        setPageState('loading')
        setFetchError('')
        try {
            const res = await fetch(BASE_URL_API + 'payrollDemoGetStatus', {
                method: 'POST', cache: 'no-store',
                body: JSON.stringify({ token: authCtx?.token, storeIdentifier }),
            })
            if (!res.ok) throw new Error('Server Error')
            const result = await res.json()

            if (result?.status === 'success' && result?.message === 'statusFetched') {
                const state = result?.response?.state
                if (state === 'enrolled') { navigate('/payroll/overview', { replace: true }); return }
                if (state === 'bookedDemo') {
                    setExistingBooking(result.response.booking)
                    setIsRescheduling(false)
                    setShowCancelConfirm(false)
                    setPageState('bookedDemo')
                    return
                }
                setPageState('notEnrolled')
                return
            }
            if (result?.message === 'tokenExpired' || result?.message === 'invalidToken') { authCtx.logout(); return }
            if (result?.message === 'storeIdentifierRequired') {
                console.error('[ScheduleADemo] storeIdentifierRequired — check caller')
                setFetchError('Something went wrong. Please try again.')
            } else if (result?.message === 'dbError') {
                console.error('[ScheduleADemo] dbError from backend')
                setFetchError('A server error occurred. Please retry.')
            } else {
                setFetchError(result?.message || 'Unable to fetch status.')
            }
            setPageState('error')
        } catch {
            setFetchError('Unable to connect to server. Please try again.')
            setPageState('error')
        }
    }, [authCtx, storeIdentifier, navigate])

    // ── API: payrollDemoGetAvailableSlots ───────────────────────
    const fetchAvailableSlots = useCallback(async () => {
        if (!storeIdentifier) return
        setSlotsLoading(true)
        setSelectedDate(null)
        setSelectedTime('')
        try {
            const res = await fetch(BASE_URL_API + 'payrollDemoGetAvailableSlots', {
                method: 'POST', cache: 'no-store',
                body: JSON.stringify({ token: authCtx?.token, storeIdentifier }),
            })
            if (!res.ok) throw new Error('Server Error')
            const result = await res.json()
            if (result?.status === 'success' && result?.message === 'slotsFetched') {
                setAvailableSlots(result.response?.slots || [])
                return
            }
            if (result?.message === 'tokenExpired' || result?.message === 'invalidToken') { authCtx.logout(); return }
            if (result?.errorCode === 'BIZ_002') {
                toast.error('Slot service is temporarily unavailable. Please contact support.')
                return
            }
            console.error('[ScheduleADemo] fetchAvailableSlots:', result?.message)
            toast.error('Could not load available slots.')
        } catch {
            toast.error('Could not load available slots. Please try again.')
        } finally {
            setSlotsLoading(false)
        }
    }, [authCtx, storeIdentifier])

    // ── API: payrollDemoBookASlot ──────────────────────────────
    const bookSlot = async () => {
        if (!isValid) { toast.error('Please select a date and time slot.'); return }
        if (!contactPhone.trim()) { toast.error('Please enter your contact phone number.'); return }
        if (!contactEmail.trim()) { toast.error('Please enter your email address.'); return }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail.trim())) {
            toast.error('Please enter a valid email address.')
            return
        }
        setIsSubmitting(true)
        try {
            const res = await fetch(BASE_URL_API + 'payrollDemoBookASlot', {
                method: 'POST', cache: 'no-store',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier,
                    slotDate:     selectedDateStr,
                    slotTime:     selectedTime,
                    contactName:  contactName.trim() || storeName || '',
                    contactPhone: contactPhone.trim(),
                    contactEmail: contactEmail.trim(),
                    notes:        message.trim(),
                }),
            })
            if (!res.ok) throw new Error('Server Error')
            const result = await res.json()
            if (result?.status === 'success' && result?.message === 'slotBooked') {
                setConfirmationCode(result.response?.confirmationCode || '')
                setExistingBooking(result.response)
                setSubmitted(true)
                return
            }
            if (result?.message === 'tokenExpired' || result?.message === 'invalidToken') { authCtx.logout(); return }
            if (result?.errorCode === 'BIZ_001') { navigate('/payroll/overview', { replace: true }); return }
            dispatchErrorCode(result?.errorCode, result?.message, {
                refetchSlots:  fetchAvailableSlots,
                refetchStatus: fetchDemoStatus,
            })
        } catch {
            toast.error('Unable to connect. Please try again.')
        } finally {
            setIsSubmitting(false)
        }
    }

    // ── API: payrollDemoRescheduleADemo ────────────────────────
    const rescheduleSlot = async () => {
        if (!isValid || !existingBooking?.bookingUuid) return
        setIsSubmitting(true)
        try {
            const res = await fetch(BASE_URL_API + 'payrollDemoRescheduleADemo', {
                method: 'POST', cache: 'no-store',
                body: JSON.stringify({
                    token:        authCtx?.token,
                    storeIdentifier,
                    bookingUuid:  existingBooking.bookingUuid,
                    newSlotDate:  selectedDateStr,
                    newSlotTime:  selectedTime,
                }),
            })
            if (!res.ok) throw new Error('Server Error')
            const result = await res.json()
            if (result?.status === 'success' && result?.message === 'demoRescheduled') {
                toast.success('Demo rescheduled successfully!')
                await fetchDemoStatus()
                return
            }
            if (result?.message === 'tokenExpired' || result?.message === 'invalidToken') { authCtx.logout(); return }
            dispatchErrorCode(result?.errorCode, result?.message, {
                refetchSlots:  fetchAvailableSlots,
                refetchStatus: () => { setIsRescheduling(false); fetchDemoStatus() },
            })
        } catch {
            toast.error('Unable to connect. Please try again.')
        } finally {
            setIsSubmitting(false)
        }
    }

    // ── API: payrollDemoCancelADemo ─────────────────────────────
    const cancelSlot = async () => {
        if (!existingBooking?.bookingUuid) return
        setIsCancelling(true)
        try {
            const res = await fetch(BASE_URL_API + 'payrollDemoCancelADemo', {
                method: 'POST', cache: 'no-store',
                body: JSON.stringify({
                    token:               authCtx?.token,
                    storeIdentifier,
                    bookingUuid:         existingBooking.bookingUuid,
                    cancellationReason:  cancellationReason.trim() || 'User requested cancellation',
                }),
            })
            if (!res.ok) throw new Error('Server Error')
            const result = await res.json()
            if (result?.status === 'success' && result?.message === 'demoCancelled') {
                toast.success('Demo cancelled.')
                setShowCancelConfirm(false)
                setCancellationReason('')
                await fetchDemoStatus()
                return
            }
            if (result?.message === 'tokenExpired' || result?.message === 'invalidToken') { authCtx.logout(); return }
            dispatchErrorCode(result?.errorCode, result?.message, { refetchStatus: fetchDemoStatus })
        } catch {
            toast.error('Unable to connect. Please try again.')
        } finally {
            setIsCancelling(false)
        }
    }

    // ── Form submit dispatcher ───────────────────────────────────
    const handleSubmit = (e) => {
        e.preventDefault()
        if (isRescheduling) rescheduleSlot()
        else bookSlot()
    }

    // ── Effects ──────────────────────────────────────────────
    useEffect(() => { fetchDemoStatus() }, [fetchDemoStatus])

    useEffect(() => {
        if (pageState === 'notEnrolled' || (pageState === 'bookedDemo' && isRescheduling)) {
            fetchAvailableSlots()
        }
    }, [pageState, isRescheduling, fetchAvailableSlots])

    // ── Loading ──────────────────────────────────────────────
    if (pageState === 'loading') {
        return (
            <div className={styles.pageShell}>
                <header className={styles.topBar}>
                    <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}><ChevronLeft size={18} /></button>
                    <span className={styles.topBarBrand}>Lokaci Pro · Payroll</span>
                    <div />
                </header>
                <div className={styles.skeletonShell}>
                    <div className={styles.skeletonCard}>
                        <div className={`${styles.skeletonLine} ${styles.skeletonLineShort}`} />
                        <div className={`${styles.skeletonLine} ${styles.skeletonLineFull}`} />
                        <div className={`${styles.skeletonLine} ${styles.skeletonLineMid}`} />
                        <div className={styles.skeletonBlock} />
                    </div>
                </div>
            </div>
        )
    }

    // ── Error ────────────────────────────────────────────────
    if (pageState === 'error') {
        return (
            <div className={styles.pageShell}>
                <header className={styles.topBar}>
                    <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}><ChevronLeft size={18} /></button>
                    <span className={styles.topBarBrand}>Lokaci Pro · Payroll</span>
                    <div />
                </header>
                <div className={styles.errorShell}>
                    <div className={styles.errorIcon}>⚠️</div>
                    <h2 className={styles.errorTitle}>Couldn't load your demo status</h2>
                    <p className={styles.errorBody}>{fetchError}</p>
                    <button type="button" className={styles.primaryBtn} onClick={fetchDemoStatus}>
                        <LoaderCircle size={15} /> Retry
                    </button>
                </div>
            </div>
        )
    }

    // ── Booked demo card ────────────────────────────────────
    if (pageState === 'bookedDemo' && !isRescheduling) {
        const slotDateFmt = existingBooking?.slotDate
            ? new Date(existingBooking.slotDate + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
            : '—'
        const slotTimeFmt = existingBooking?.slotTime ? formatSlotTime(existingBooking.slotTime) : '—'
        return (
            <div className={styles.pageShell}>
                <header className={styles.topBar}>
                    <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}><ChevronLeft size={18} /></button>
                    <span className={styles.topBarBrand}>Lokaci Pro · Payroll</span>
                    <div />
                </header>
                <div className={styles.bookedShell}>
                    <div className={styles.bookedCard}>
                        <div className={styles.bookedIconWrap}><CalendarCheck2 size={32} /></div>
                        <span className={styles.bookedKicker}><Sparkles size={13} /> Demo Scheduled</span>
                        <h1 className={styles.bookedTitle}>You're all set!</h1>
                        <p className={styles.bookedSub}>Your payroll demo is confirmed. Our expert will call you at the time below.</p>

                        <div className={styles.bookedMeta}>
                            <div className={styles.bookedMetaRow}>
                                <span className={styles.bookedMetaLabel}><CalendarCheck2 size={13} /> Date</span>
                                <span className={styles.bookedMetaValue}>{slotDateFmt}</span>
                            </div>
                            <div className={styles.bookedMetaRow}>
                                <span className={styles.bookedMetaLabel}><Clock3 size={13} /> Time</span>
                                <span className={styles.bookedMetaValue}>{slotTimeFmt}</span>
                            </div>
                            {existingBooking?.contactName && (
                                <div className={styles.bookedMetaRow}>
                                    <span className={styles.bookedMetaLabel}><User size={13} /> Contact</span>
                                    <span className={styles.bookedMetaValue}>{existingBooking.contactName}</span>
                                </div>
                            )}
                            {existingBooking?.contactPhone && (
                                <div className={styles.bookedMetaRow}>
                                    <span className={styles.bookedMetaLabel}><Phone size={13} /> Phone</span>
                                    <span className={styles.bookedMetaValue}>{existingBooking.contactPhone}</span>
                                </div>
                            )}
                            <div className={styles.bookedMetaRow}>
                                <span className={styles.bookedMetaLabel}><Bookmark size={13} /> Status</span>
                                <span className={`${styles.bookedMetaValue} ${styles.statusBadgePending}`}>
                                    {existingBooking?.status || 'pending'}
                                </span>
                            </div>
                        </div>

                        {showCancelConfirm ? (
                            <div className={styles.cancelConfirmBox}>
                                <p className={styles.cancelConfirmTitle}>Cancel your demo?</p>
                                <div className={styles.fieldGroup}>
                                    <label className={styles.label}>Reason <span className={styles.optionalTag}>optional</span></label>
                                    <textarea
                                        className={`${styles.input} ${styles.textarea}`}
                                        rows={2}
                                        placeholder="e.g. Schedule conflict, not ready yet..."
                                        value={cancellationReason}
                                        onChange={e => setCancellationReason(e.target.value)}
                                    />
                                </div>
                                <div className={styles.cancelConfirmActions}>
                                    <button type="button" className={styles.cancelDemoBtn} onClick={cancelSlot} disabled={isCancelling}>
                                        {isCancelling
                                            ? <><LoaderCircle size={14} className={styles.spinIcon} /> Cancelling...</>
                                            : 'Yes, cancel demo'}
                                    </button>
                                    <button type="button" className={styles.rescheduleBtn}
                                        onClick={() => { setShowCancelConfirm(false); setCancellationReason('') }}
                                    >
                                        Keep my booking
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className={styles.bookedActions}>
                                <button
                                    type="button"
                                    className={styles.rescheduleBtn}
                                    onClick={() => { setIsRescheduling(true); setSelectedDate(null); setSelectedTime('') }}
                                >
                                    <CalendarCheck2 size={15} /> Reschedule
                                </button>
                                <button
                                    type="button"
                                    className={styles.cancelDemoBtn}
                                    onClick={() => setShowCancelConfirm(true)}
                                >
                                    Cancel demo
                                </button>
                            </div>
                        )}
                        <p className={styles.bookedFine}>
                            Need help? Call us at{' '}
                            <a href="tel:+91-9999648406" className={styles.bookedHelpLink}>+91-9999648406</a>
                        </p>
                    </div>
                </div>
            </div>
        )
    }

    // ── Post-submission success screen ──────────────────────────
    if (submitted) {
        return (
            <div className={styles.pageShell}>
                <header className={styles.topBar}>
                    <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}><ChevronLeft size={18} /></button>
                    <span className={styles.topBarBrand}>Lokaci Pro · Payroll</span>
                    <div />
                </header>
                <div className={styles.successShell}>
                    <div className={styles.successIcon}><CalendarCheck2 size={38} /></div>
                    <span className={styles.successKicker}><Sparkles size={13} /> Booked!</span>
                    <h1 className={styles.successTitle}>Your demo is confirmed.</h1>
                    <p className={styles.successBody}>
                        Our team will call you to confirm the session. If you need to reschedule, just call us.
                    </p>
                    <div className={styles.successMeta}>
                        {storeName && <span><Building2 size={13} /> {storeName}</span>}
                        <span><CalendarCheck2 size={13} /> {formatDate(selectedDate)}</span>
                        <span><Clock3 size={13} /> {formatSlotTime(selectedTime)}</span>
                        {confirmationCode && <span><Ticket size={13} /> {confirmationCode}</span>}
                    </div>
                    <button type="button" className={styles.primaryBtn} onClick={() => navigate('/payroll/overview')}>
                        Back to Payroll <ArrowRight size={15} />
                    </button>
                </div>
            </div>
        )
    }

    // ── Booking / reschedule form (notEnrolled or bookedDemo+isRescheduling) ─
    return (
        <div className={styles.pageShell}>

            <header className={styles.topBar}>
                <button
                    type="button"
                    className={styles.backBtn}
                    onClick={() => isRescheduling ? setIsRescheduling(false) : navigate(-1)}
                >
                    <ChevronLeft size={18} />
                </button>
                <span className={styles.topBarBrand}>Lokaci Pro · Payroll</span>
                <div />
            </header>

            <section className={styles.heroSection}>
                <div className={styles.heroInner}>
                    <div className={styles.heroLeft}>
                        <span className={styles.heroKicker}>
                            <Sparkles size={13} /> {isRescheduling ? 'Reschedule your demo' : 'Free 30-minute demo'}
                        </span>
                        <h1 className={styles.heroTitle}>
                            {isRescheduling
                                ? <>Pick a new slot —<br /><span className={styles.heroAccent}>we'll update your booking.</span></>
                                : <>Dekho Lokaci Payroll live —<br /><span className={styles.heroAccent}>apne salon ke numbers ke saath.</span></>}
                        </h1>
                        <p className={styles.heroSubtitle}>
                            {isRescheduling
                                ? 'Select a new date and time. Your booking status will reset to pending after rescheduling.'
                                : 'Our payroll expert will walk you through a live setup — attendance, commissions, PF/ESI — using your actual salon structure. No generic pitch. Just your numbers.'}
                        </p>
                        {isRescheduling && existingBooking ? (
                            <div className={styles.currentSlotBanner}>
                                <span className={styles.currentSlotLabel}>Current slot:</span>
                                <span>
                                    {new Date(existingBooking.slotDate + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
                                    {' at '}{formatSlotTime(existingBooking.slotTime)}
                                </span>
                            </div>
                        ) : (
                            <ul className={styles.trustList}>
                                {TRUST_POINTS.map((item, index) => {
                                    const Icon = item.icon
                                    return (
                                        <li key={index} className={styles.trustItem}>
                                            <Icon size={15} className={styles.trustIcon} />
                                            {item.text}
                                        </li>
                                    )
                                })}
                            </ul>
                        )}
                    </div>

                    {/* ── Booking Card ──────────────────── */}
                    <div className={styles.formCard}>
                        <div className={styles.formCardHead}>
                            <CalendarCheck2 size={20} className={styles.formCardIcon} />
                            <div>
                                <h2 className={styles.formCardTitle}>
                                    {isRescheduling ? 'Pick a new slot' : 'Pick your slot'}
                                </h2>
                                <p className={styles.formCardSub}>
                                    {isRescheduling
                                        ? 'New slot replaces your current booking.'
                                        : 'Choose a date & time — we do the rest.'}
                                </p>
                            </div>
                        </div>

                        {storeName && (
                            <div className={styles.storeChip}>
                                <Building2 size={13} />
                                <span>{storeName}</span>
                            </div>
                        )}

                        <form className={styles.form} onSubmit={handleSubmit} noValidate>
                            <div className={styles.bookingColumns}>

                                {/* ── Left: Calendar ─────────── */}
                                <div className={styles.calSection}>
                                    <p className={styles.calSectionLabel}>
                                        <CalendarCheck2 size={13} /> Select a date
                                        {selectedDate && (
                                            <span className={styles.calSelectedBadge}>
                                                {selectedDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                                            </span>
                                        )}
                                    </p>
                                    <CalendarPicker
                                        selectedDate={selectedDate}
                                        onSelect={(d) => { setSelectedDate(d); setSelectedTime('') }}
                                        availableDates={availableDates}
                                        slotsLoading={slotsLoading}
                                    />
                                </div>

                                {/* ── Right: Slots + Contact + CTA ── */}
                                <div className={styles.bookingRight}>
                                    <div className={styles.slotsSection}>
                                        <p className={styles.calSectionLabel}>
                                            <Clock3 size={13} />
                                            {selectedDate
                                                ? <><span>Slots for </span><strong>{selectedDate.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}</strong></>
                                                : <span className={styles.slotsPlaceholderLabel}>
                                                    {slotsLoading ? 'Loading slots...' : 'Pick a date first'}
                                                  </span>
                                            }
                                        </p>
                                        {slotsLoading ? (
                                            <div className={styles.slotsLoadingWrap}>
                                                <LoaderCircle size={16} className={styles.spinIcon} />
                                                <span>Loading available slots…</span>
                                            </div>
                                        ) : selectedDate ? (
                                            slotsForSelectedDate.length > 0 ? (
                                                <div className={styles.slotsGrid}>
                                                    {slotsForSelectedDate.map((slot) => (
                                                        <button
                                                            key={slot.time}
                                                            type="button"
                                                            className={[
                                                                styles.slotPill,
                                                                selectedTime === slot.time ? styles.slotPillActive : '',
                                                            ].join(' ')}
                                                            onClick={() => setSelectedTime(slot.time)}
                                                        >
                                                            {formatSlotTime(slot.time)}
                                                        </button>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className={styles.noSlotsMsg}>No slots available for this date. Please pick another.</p>
                                            )
                                        ) : (
                                            <p className={styles.noSlotsMsg}>← Select a date to see available slots</p>
                                        )}
                                    </div>

                                    {/* Contact details (only for new booking) */}
                                    {!isRescheduling && (
                                        <div className={styles.contactSection}>
                                            <p className={styles.calSectionLabel}><Phone size={13} /> Contact details</p>
                                            <div className={styles.contactRow}>
                                                <div className={styles.fieldGroup}>
                                                    <label className={styles.label}>Email *</label>
                                                    <input
                                                        type="email"
                                                        className={styles.input}
                                                        placeholder="your@email.com"
                                                        value={contactEmail}
                                                        onChange={e => setContactEmail(e.target.value)}
                                                    />
                                                </div>
                                                <div className={styles.fieldGroup}>
                                                    <label className={styles.label}>Phone *</label>
                                                    <input
                                                        type="tel"
                                                        className={styles.input}
                                                        placeholder="Mobile number"
                                                        maxLength={13}
                                                        value={contactPhone}
                                                        onChange={e => setContactPhone(e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className={styles.fieldGroup}>
                                                <label className={styles.label}>Anything to cover? <span className={styles.optionalTag}>optional</span></label>
                                                <input
                                                    type="text"
                                                    className={styles.input}
                                                    placeholder="e.g. Commission slabs, PF setup…"
                                                    value={message}
                                                    onChange={(e) => setMessage(e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {/* Notes (reschedule mode only) */}
                                    {isRescheduling && (
                                        <div className={styles.fieldGroup}>
                                            <label className={styles.label}>Anything to cover? <span className={styles.optionalTag}>optional</span></label>
                                            <input
                                                type="text"
                                                className={styles.input}
                                                placeholder="e.g. Commission slabs, PF setup…"
                                                value={message}
                                                onChange={(e) => setMessage(e.target.value)}
                                            />
                                        </div>
                                    )}

                                    {/* Summary strip */}
                                    {selectedDate && selectedTime && (
                                        <div className={styles.summaryStrip}>
                                            <span><CalendarCheck2 size={12} /> {selectedDate.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</span>
                                            <span className={styles.summaryDivider}>·</span>
                                            <span><Clock3 size={12} /> {formatSlotTime(selectedTime)}</span>
                                            {storeName && <><span className={styles.summaryDivider}>·</span><span><Building2 size={12} /> {storeName}</span></>}
                                        </div>
                                    )}

                                    <button
                                        type="submit"
                                        className={styles.submitBtn}
                                        disabled={isSubmitting || !isValid}
                                    >
                                        {isSubmitting
                                            ? <><LoaderCircle size={16} className={styles.spinIcon} /> {isRescheduling ? 'Rescheduling...' : 'Booking your slot...'}</>
                                            : isRescheduling
                                                ? <><CalendarCheck2 size={15} /> Confirm Reschedule</>
                                                : <><Send size={15} /> Confirm My Free Demo</>}
                                    </button>
                                    <p className={styles.formFinePrint}>
                                        {isRescheduling
                                            ? 'Status resets to pending after rescheduling.'
                                            : 'No card required · We confirm within a few hours.'}
                                    </p>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </section>

            {!isRescheduling && (
                <>
                    <section className={styles.highlightsSection}>
                        <div className={styles.sectionContainer}>
                            <div className={styles.sectionHead}>
                                <span className={styles.sectionKicker}><Sparkles size={13} /> What happens in the session</span>
                                <h2 className={styles.sectionTitle}>30 minutes. Zero fluff. Real outcomes.</h2>
                                <p className={styles.sectionSubtitle}>Each demo is custom — we walk through your exact setup, not a generic slide deck.</p>
                            </div>
                            <div className={styles.highlightsGrid}>
                                {DEMO_HIGHLIGHTS.map((item, index) => {
                                    const Icon = item.icon
                                    return (
                                        <div key={index} className={styles.highlightCard}>
                                            <div className={styles.highlightIconWrap}><Icon size={20} /></div>
                                            <div>
                                                <h3 className={styles.highlightTitle}>{item.title}</h3>
                                                <p className={styles.highlightBody}>{item.body}</p>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    </section>

                    <section className={styles.bottomCta}>
                        <div className={styles.sectionContainer}>
                            <p className={styles.bottomCtaText}>Still not sure? Just call us.</p>
                            <a href="tel:+91-9999648406" className={styles.callLink}>
                                <Phone size={15} /> Talk to our payroll team
                            </a>
                            <p className={styles.bottomCtaFine}>24x7</p>
                        </div>
                    </section>
                </>
            )}

        </div>
    )
}

export default ScheduleADemo
